import type {CiCustomerAddressRow} from "./ci-customer-address-request";

/**
 * Is an address one of the customer's own addresses, or does it belong to the object alone?
 *
 * The distinction drives the conditional "Adresse" tab of both object base components (design
 * section 15.2): an insurable object may carry an OBJECT SPECIFIC address - a rented flat, an
 * insured building the customer does not live in - which is a bare ad.Address without any
 * cr.CustomerAddress link. Exactly that case gets an editable address tab; an object pointing at
 * an address the customer already has is edited through the customer, not through the object.
 *
 * Pure and dependency free by design so it stays unit testable without a TestBed. The customer
 * address set it compares against is produced by CiCustomerAddressRequest.
 */
export class CiCustomerAddressPredicate {

    /**
     * Does the address belong to the customer?
     *
     * @param addressId        the ad.Address the object points at (read from its
     *                         gen_<typeFieldId>DtoId field), null/undefined when the object has none
     * @param customerAddresses the ACTIVE customer addresses; null/empty means the customer has none
     * @returns true when addressId is part of the customer address set. A missing addressId is
     *          false - the object has no address at all, so there is nothing that could be the
     *          customer's, and the callers must not read that as "object specific".
     */
    static isCustomerAddress(
        addressId: number | undefined | null,
        customerAddresses: CiCustomerAddressRow[] | undefined | null): boolean {

        if (addressId == null || !customerAddresses || !customerAddresses.length) {
            return false;
        }

        return customerAddresses.some(row => row && row.addressId === addressId);
    }

    /**
     * The complement used by the tab condition: the object has an address AND that address is not
     * one of the customer's.
     *
     * Deliberately NOT the negation of isCustomerAddress - an object without any address must
     * answer false here as well, otherwise every address-less object would show an empty address
     * tab.
     */
    static isObjectSpecificAddress(
        addressId: number | undefined | null,
        customerAddresses: CiCustomerAddressRow[] | undefined | null): boolean {

        if (addressId == null) {
            return false;
        }

        return !CiCustomerAddressPredicate.isCustomerAddress(addressId, customerAddresses);
    }

}
